import { useState, useEffect } from 'react'
import { GlobalPriceHistoryEntry, STORES } from '../types'
import { priceHistoryApi } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import { formatPrice, formatDateTime } from '../utils/formatters'
import toast from 'react-hot-toast'

const PriceHistoryPage = () => {
  const [entries, setEntries] = useState<GlobalPriceHistoryEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchProduct, setSearchProduct] = useState('')
  const [selectedStore, setSelectedStore] = useState<string>('all')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [limit, setLimit] = useState(100)

  useEffect(() => {
    loadPriceHistory()
  }, [])

  const loadPriceHistory = async () => {
    setIsLoading(true)
    try {
      const filters = {
        productName: searchProduct || undefined,
        store: selectedStore === 'all' ? undefined : selectedStore,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        limit: limit
      }

      const response = await priceHistoryApi.getPriceHistory(filters)
      if (response.success && response.data) {
        setEntries(response.data)
      } else {
        toast.error(response.error || 'Failed to load price history')
      }
    } catch (error) {
      toast.error('Failed to load price history')
      console.error('Error loading price history:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSearch = () => {
    loadPriceHistory()
  }

  const getPriceChangeColor = (entry: GlobalPriceHistoryEntry) => {
    if (!entry.oldPrice) return 'text-blue-600' // New product
    if (entry.newPrice > entry.oldPrice) return 'text-red-600' // Price increased
    if (entry.newPrice < entry.oldPrice) return 'text-green-600' // Price decreased
    return 'text-gray-600' // No change
  }

  const getPriceChangeIcon = (entry: GlobalPriceHistoryEntry) => {
    if (!entry.oldPrice) return '★' // New product
    if (entry.newPrice > entry.oldPrice) return '↗' // Price increased
    if (entry.newPrice < entry.oldPrice) return '↘' // Price decreased
    return '→' // No change
  }

  const getPriceChangeText = (entry: GlobalPriceHistoryEntry) => {
    if (!entry.oldPrice) return 'Uusi tuote'
    const change = entry.newPrice - entry.oldPrice
    const changePercent = ((change / entry.oldPrice) * 100).toFixed(1)
    if (change > 0) return `+${formatPrice(change)} (+${changePercent}%)`
    if (change < 0) return `${formatPrice(change)} (${changePercent}%)`
    return 'Ei muutosta'
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Hintahistoria</h1>
          <p className="text-gray-600 mt-1">Seuraa tuotteiden hintojen muutoksia</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Suodattimet</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label htmlFor="product-search" className="block text-sm font-medium text-gray-700 mb-1">
              Tuote
            </label>
            <input
              id="product-search"
              type="text"
              value={searchProduct}
              onChange={(e) => setSearchProduct(e.target.value)}
              placeholder="Etsi tuotetta..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label htmlFor="store-select" className="block text-sm font-medium text-gray-700 mb-1">
              Kauppa
            </label>
            <select
              id="store-select"
              value={selectedStore}
              onChange={(e) => setSelectedStore(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">Kaikki kaupat</option>
              {STORES.filter(store => store !== 'all').map(store => (
                <option key={store} value={store}>{store}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 mb-1">
              Alkaen
            </label>
            <input
              id="start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 mb-1">
              Päättyen
            </label>
            <input
              id="end-date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row gap-4 items-end">
          <div>
            <label htmlFor="limit" className="block text-sm font-medium text-gray-700 mb-1">
              Näytä enintään
            </label>
            <select
              id="limit"
              value={limit}
              onChange={(e) => setLimit(parseInt(e.target.value))}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value={50}>50 tulosta</option>
              <option value={100}>100 tulosta</option>
              <option value={200}>200 tulosta</option>
              <option value={500}>500 tulosta</option>
            </select>
          </div>
          <button
            onClick={handleSearch}
            className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
          >
            Hae
          </button>
        </div>
      </div>

      {/* Results */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            Hintamuutokset ({entries.length})
          </h2>
        </div>

        {entries.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Ei hintahistoriaa</h3>
            <p className="text-gray-600">Hintamuutokset tallennetaan kun tuotteiden hintoja muutetaan.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tuote
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kauppa
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vanha hinta
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Uusi hinta
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Muutos
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Päivämäärä
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Lähde
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {entries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {entry.productName}
                      </div>
                      {entry.storeName && (
                        <div className="text-sm text-gray-500">
                          {entry.storeName}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {entry.store}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {entry.oldPrice ? formatPrice(entry.oldPrice) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatPrice(entry.newPrice)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`flex items-center text-sm font-medium ${getPriceChangeColor(entry)}`}>
                        <span className="mr-1">{getPriceChangeIcon(entry)}</span>
                        {getPriceChangeText(entry)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDateTime(entry.timestamp)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        entry.updatedBy === 'photo-ai' 
                          ? 'bg-blue-100 text-blue-800'
                          : entry.updatedBy === 'user'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {entry.updatedBy === 'photo-ai' ? 'AI' : 
                         entry.updatedBy === 'user' ? 'Käyttäjä' : 'Manuaalinen'}
                      </span>
                      {entry.confidence && (
                        <div className="text-xs text-gray-500 mt-1">
                          {Math.round(entry.confidence * 100)}% varmuus
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default PriceHistoryPage